interface NavigatorUAData {
  platform?: string;
  brands?: Array<{ brand: string; version: string }>;
  mobile?: boolean;
}

declare global {
  interface Navigator {
    userAgentData?: NavigatorUAData;
  }
}

export interface BrowserInfo {
  name: string;
  version: string;
  majorVersion: number;
  isSmartTV: boolean;
  isLegacyTV: boolean;
  platform: string;
  supportsModernJS: boolean;
  webOSVersion?: number | undefined;
  tizenVersion?: number | undefined;
  chromeVersion?: number | undefined;
}

function getChromeVersion(ua: string): number {
  const match = ua.match(/Chrome\/(\d+)/);
  return match && match[1] ? parseInt(match[1], 10) : 0;
}

function getWebKitVersion(ua: string): number {
  const match = ua.match(/AppleWebKit\/(\d+)/);
  return match && match[1] ? parseInt(match[1], 10) : 0;
}

function getPlatform(): string {
  if ('userAgentData' in navigator && navigator.userAgentData?.platform) {
    return navigator.userAgentData.platform;
  }

  const ua = navigator.userAgent;
  if (/Mac|iPhone|iPad|iPod/i.test(ua)) {
    return /iPhone|iPad|iPod/i.test(ua) ? 'iPhone' : 'MacIntel';
  }
  if (/Win/i.test(ua)) {
    return 'Win32';
  }
  if (/Linux/i.test(ua)) {
    return /Android/i.test(ua) ? 'Linux armv8l' : 'Linux x86_64';
  }
  if (/CrOS/i.test(ua)) {
    return 'CrOS';
  }

  // eslint-disable-next-line deprecation/deprecation
  return (navigator as any).platform || 'Unknown';
}

export function detectBrowser(): BrowserInfo {
  const ua = navigator.userAgent;
  const platform = getPlatform();

  let name = 'Unknown';
  let version = '0';
  let majorVersion = 0;
  let isSmartTV = false;
  let isLegacyTV = false;
  let supportsModernJS = true;
  let webOSVersion: number | undefined;
  let tizenVersion: number | undefined;
  let chromeVersionNum: number | undefined;

  const chromeVersion = getChromeVersion(ua);
  const webkitVersion = getWebKitVersion(ua);
  chromeVersionNum = chromeVersion > 0 ? chromeVersion : undefined;

  if (/Web0S|webOS|LG Browser|LGSTB/i.test(ua)) {
    name = 'LG WebOS';
    isSmartTV = true;

    let match = ua.match(/Web0S[/\s]*([\d.]+)/i) || ua.match(/webOS[/\s]*([\d.]+)/i);

    if (!match || !match[1]) {
      match = ua.match(/webOSTV[/\s-]*([\d.]+)/i) || ua.match(/webOS\.TV[/\s-]*([\d.]+)/i);
    }

    if (match && match[1]) {
      version = match[1];
      const parts = version.split('.');
      majorVersion = parts[0] ? parseInt(parts[0], 10) : 0;
      webOSVersion = majorVersion;
    } else if (chromeVersion > 0) {
      if (chromeVersion >= 79) {
        webOSVersion = 6;
        version = '6.0';
        majorVersion = 6;
      } else if (chromeVersion >= 68) {
        webOSVersion = 5;
        version = '5.0';
        majorVersion = 5;
      } else if (chromeVersion >= 53) {
        webOSVersion = 4;
        version = '4.0';
        majorVersion = 4;
      } else if (chromeVersion >= 38) {
        webOSVersion = 3;
        version = '3.0';
        majorVersion = 3;
      } else {
        webOSVersion = 2;
        version = '2.0';
        majorVersion = 2;
      }
    } else {
      version = 'Unknown';
      webOSVersion = undefined;
    }

    isLegacyTV = webOSVersion !== undefined && webOSVersion < 3;
  } else if (/Tizen/i.test(ua)) {
    name = 'Samsung Tizen';
    isSmartTV = true;
    const match = ua.match(/Tizen[/\s]*([\d.]+)/i);
    version = match && match[1] ? match[1] : 'Unknown';
    if (version !== 'Unknown') {
      const parts = version.split('.');
      majorVersion = parts[0] ? parseInt(parts[0], 10) : 0;
      tizenVersion = majorVersion;
    }

    isLegacyTV = tizenVersion !== undefined && tizenVersion < 3;
  } else if (/SMART-TV|SmartTV/i.test(ua)) {
    name = 'Smart TV';
    isSmartTV = true;
    isLegacyTV = chromeVersion > 0 && chromeVersion < 53;
  } else if (/NetCast/i.test(ua)) {
    name = 'LG NetCast';
    isSmartTV = true;
    isLegacyTV = true;
  } else if (/BRAVIA/i.test(ua)) {
    name = 'Sony BRAVIA';
    isSmartTV = true;
    isLegacyTV = chromeVersion > 0 && chromeVersion < 53;
  } else {
    if (chromeVersion > 0) {
      name = 'Chrome';
      version = chromeVersion.toString();
      majorVersion = chromeVersion;

      if (chromeVersion < 50) {
        supportsModernJS = false;
      }
    }

    if (webkitVersion > 0 && webkitVersion < 600) {
      supportsModernJS = false;
    }
  }

  if (typeof Promise === 'undefined' ||
      typeof Map === 'undefined' ||
      typeof Set === 'undefined') {
    supportsModernJS = false;
  }

  if (typeof URLSearchParams === 'undefined') {
    supportsModernJS = false;
  }

  return {
    name,
    version,
    majorVersion,
    isSmartTV,
    isLegacyTV,
    platform,
    supportsModernJS,
    webOSVersion,
    tizenVersion,
    chromeVersion: chromeVersionNum,
  };
}

export function getBrowserConfigOverrides(): {
  allowNativeHls?: boolean;
} {
  const browser = detectBrowser();
  const overrides: { allowNativeHls?: boolean } = {};

  if (browser.isSmartTV) {
    overrides.allowNativeHls = true;
  }

  return overrides;
}

export function supportsModernJS(): boolean {
  try {
    return (
      typeof Promise !== 'undefined' &&
      typeof Map !== 'undefined' &&
      typeof Set !== 'undefined' &&
      typeof Array.from !== 'undefined' &&
      typeof Object.assign !== 'undefined' &&
      typeof Array.prototype.forEach !== 'undefined' &&
      typeof String.prototype.includes !== 'undefined'
    );
  } catch (e) {
    return false;
  }
}

export function logBrowserInfo(debug: boolean = false): void {
  if (!debug) return;

  const browser = detectBrowser();

  console.log('[StormcloudVideoPlayer] Browser Compatibility Info:', {
    browser: `${browser.name} ${browser.version}`,
    platform: browser.platform,
    isSmartTV: browser.isSmartTV,
    isLegacyTV: browser.isLegacyTV,
    supportsModernJS: browser.supportsModernJS,
    ...(browser.webOSVersion !== undefined ? { webOSVersion: browser.webOSVersion } : {}),
    ...(browser.tizenVersion !== undefined ? { tizenVersion: browser.tizenVersion } : {}),
    ...(browser.chromeVersion !== undefined ? { chromeVersion: browser.chromeVersion } : {}),
    userAgent: navigator.userAgent,
  });
}

export function supportsFeature(feature: string): boolean {
  switch (feature) {
    case 'urlsearchparams':
      return typeof URLSearchParams !== 'undefined';
    case 'textencoder':
      return typeof TextEncoder !== 'undefined';
    case 'promises':
      return typeof Promise !== 'undefined';
    case 'fetch':
      return typeof fetch !== 'undefined';
    case 'crypto':
      return typeof crypto !== 'undefined' && typeof crypto.subtle !== 'undefined';
    default:
      return false;
  }
}
